import { useState, useEffect, useMemo } from 'react';
import {
  Receipt, TrendingDown, Plus, RefreshCw, AlertTriangle, Info,
  ArrowRight, Loader2, Calculator, Activity, Sliders, X, Repeat,
} from 'lucide-react';
import { Spinner, InfoTip } from '../components/ui';
import {
  getTaxLots, getTaxHarvest, postWhatIf,
} from '../services/api';

const TABS = [
  { id: 'harvest', label: 'Tax-Loss Harvest', icon: TrendingDown },
  { id: 'whatif',  label: 'What-If Scenarios', icon: Calculator },
  { id: 'lots',    label: 'My Lots',           icon: Activity },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function fmtUsd(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function fmtPct(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}

// ─── Tax-Loss Harvest tab ────────────────────────────────────────────────────

function HarvestTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxRatePct, setTaxRatePct] = useState(30);
  const [minHarvestUsd, setMinHarvestUsd] = useState(25);

  async function load() {
    setLoading(true);
    try {
      const res = await getTaxHarvest({ taxRatePct, minHarvestUsd });
      setData(res?.data || res);
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taxRatePct, minHarvestUsd]);

  if (loading && !data) return <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>;

  const ops = data?.opportunities || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
              Effective tax rate (federal + state, illustrative)
              <InfoTip side="bottom">Your combined federal + state marginal rate for this gain category. US mid-bracket ≈ 30%, high-income CA ≈ 37%, long-term gains no-tax state ≈ 15%. Non-US: estimate your local equivalent.</InfoTip>
            </label>
            <div className="flex items-center gap-3">
              <input type="range" min={10} max={50} step={1} value={taxRatePct}
                onChange={e => setTaxRatePct(Number(e.target.value))} className="flex-1 accent-accent-primary" />
              <span className="text-white font-mono text-sm w-12 text-right">{taxRatePct}%</span>
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
              Minimum harvest amount ($)
              <InfoTip side="bottom">Hides opportunities smaller than this loss. Default $25 filters trivial harvests where fees eat savings. Raise to $100+ for cleaner output if you have many tiny lots; lower to $5 if even small losses matter at your tax rate.</InfoTip>
            </label>
            <div className="flex items-center gap-3">
              <input type="range" min={5} max={500} step={5} value={minHarvestUsd}
                onChange={e => setMinHarvestUsd(Number(e.target.value))} className="flex-1 accent-accent-primary" />
              <span className="text-white font-mono text-sm w-16 text-right">${minHarvestUsd}</span>
            </div>
          </div>
          <button onClick={load} className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-dark-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <p className="text-xs uppercase text-gray-500 mb-1">Harvestable opportunities</p>
          <p className="text-3xl font-black text-white">{totals.count ?? 0}</p>
          <p className="text-[10px] text-gray-600 mt-1">across {totals.openLotCount ?? 0} open lots</p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-gray-500 mb-1">Total losses you could harvest</p>
          <p className="text-3xl font-black text-red-400">{fmtUsd(totals.totalLossUsd)}</p>
          <p className="text-[10px] text-gray-600 mt-1">unrealized (you still hold the asset)</p>
        </Card>
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <p className="text-xs uppercase text-emerald-400/80 mb-1">Estimated tax savings</p>
          <p className="text-3xl font-black text-emerald-400">{fmtUsd(totals.totalEstSavingsUsd)}</p>
          <p className="text-[10px] text-emerald-400/60 mt-1">at {totals.taxRatePct}% rate · estimate, not tax advice</p>
        </Card>
      </div>

      {/* Disclosures */}
      {data?.disclosures && (
        <Card className="!p-4 border-amber-500/20 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-300/90 leading-relaxed">
              <p><strong>Tax advice:</strong> {data.disclosures.taxAdvice}</p>
              <p className="mt-1"><strong>Replacements:</strong> {data.disclosures.replacementCaveat}</p>
              <p className="mt-1"><strong>Execution:</strong> {data.disclosures.executionCaveat}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Opportunities list */}
      {ops.length === 0 ? (
        <Card className="text-center py-16">
          <TrendingDown className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No harvestable losses found at these settings.</p>
          <p className="text-xs text-gray-600 mt-1">Drop the minimum harvest amount, or check back when the market moves.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {ops.map((op, i) => (
            <Card key={`${op.lotId}-${i}`}>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white font-semibold">{op.base}</h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{op.lotTerm}</span>
                    <span className="text-[10px] text-gray-500">opened {op.openedAt ? new Date(op.openedAt).toLocaleDateString() : '—'}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Holding {op.baseRemaining.toFixed(6)} · cost basis {fmtUsd(op.costBasisUsd)} · now {fmtUsd(op.currentValueUsd)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-black text-red-400">{fmtUsd(-op.unrealizedLossUsd)}</p>
                  <p className="text-[10px] text-emerald-400">+{fmtUsd(op.estTaxSavingsUsd)} est. saved</p>
                </div>
              </div>

              {op.replacementOptions.length > 0 && (
                <div className="bg-dark-700/40 rounded-xl p-3">
                  <p className="text-[10px] uppercase text-gray-500 font-bold mb-2 flex items-center gap-1">
                    <Repeat className="w-3 h-3" /> Suggested replacement (maintains exposure, distinct token)
                  </p>
                  <div className="space-y-1.5">
                    {op.replacementOptions.map(r => (
                      <div key={r.symbol} className="flex items-center gap-3 text-xs">
                        <span className="font-mono text-emerald-400 w-20">{r.symbol}</span>
                        <span className="text-gray-300">{r.name}</span>
                        <span className="text-gray-500 flex-1 text-[10px]">— {r.rationale}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-600 mt-2 italic">
                    To execute: open the Swap or CEX trading page, sell {op.base}, buy a replacement above. haythix does not auto-execute harvest trades.
                  </p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── What-If Scenarios tab ───────────────────────────────────────────────────

// Starter presets — one-click loads a worked scenario so first-time users
// don't have to invent weight maps from scratch.
const WHATIF_PRESETS = [
  {
    id: 'add_sol',
    label: 'Add SOL (10%)',
    desc: '50/30/20 BTC/ETH/USDT → 45/30/10/15 with SOL trimmed from BTC + USDT',
    current:  [{ asset: 'BTC', weight: 0.5  }, { asset: 'ETH', weight: 0.3 }, { asset: 'USDT', weight: 0.2 }],
    proposed: [{ asset: 'BTC', weight: 0.45 }, { asset: 'ETH', weight: 0.3 }, { asset: 'SOL', weight: 0.1 }, { asset: 'USDT', weight: 0.15 }],
  },
  {
    id: 'derisk',
    label: 'De-risk to 70/30',
    desc: '60/40 risk-on → 70% stables / 30% BTC — defensive shift',
    current:  [{ asset: 'BTC', weight: 0.6 }, { asset: 'ETH', weight: 0.3 }, { asset: 'USDT', weight: 0.1 }],
    proposed: [{ asset: 'BTC', weight: 0.3 }, { asset: 'USDT', weight: 0.7 }],
  },
  {
    id: 'harvest_swap',
    label: 'Harvest ETH → WSTETH',
    desc: 'Replace ETH lot with WSTETH (Lido-staked wrapper) for tax-loss harvest while keeping exposure',
    current:  [{ asset: 'BTC', weight: 0.4 }, { asset: 'ETH', weight: 0.4 }, { asset: 'USDC', weight: 0.2 }],
    proposed: [{ asset: 'BTC', weight: 0.4 }, { asset: 'WSTETH', weight: 0.4 }, { asset: 'USDC', weight: 0.2 }],
  },
];

function WhatIfTab() {
  // Default current portfolio: 50% BTC, 30% ETH, 20% USDT
  const [currentRows, setCurrentRows] = useState([
    { asset: 'BTC',  weight: 0.5 },
    { asset: 'ETH',  weight: 0.3 },
    { asset: 'USDT', weight: 0.2 },
  ]);
  const [proposedRows, setProposedRows] = useState([
    { asset: 'BTC',  weight: 0.4 },
    { asset: 'ETH',  weight: 0.3 },
    { asset: 'SOL',  weight: 0.2 },
    { asset: 'USDT', weight: 0.1 },
  ]);
  const [lookbackDays, setLookbackDays] = useState(90);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  function applyPreset(preset) {
    setCurrentRows(preset.current.map(r => ({ ...r })));
    setProposedRows(preset.proposed.map(r => ({ ...r })));
    setResult(null);
  }

  function rowsToWeights(rows) {
    const out = {};
    for (const r of rows) {
      const a = String(r.asset || '').toUpperCase().trim();
      if (!a) continue;
      out[a] = (out[a] || 0) + Number(r.weight || 0);
    }
    return out;
  }

  const currentSum = useMemo(() => currentRows.reduce((s, r) => s + Number(r.weight || 0), 0), [currentRows]);
  const proposedSum = useMemo(() => proposedRows.reduce((s, r) => s + Number(r.weight || 0), 0), [proposedRows]);
  const sumsValid = Math.abs(currentSum - 1) < 0.001 && Math.abs(proposedSum - 1) < 0.001;

  async function compute() {
    if (!sumsValid) return;
    setLoading(true);
    try {
      const res = await postWhatIf({
        currentWeights: rowsToWeights(currentRows),
        proposedWeights: rowsToWeights(proposedRows),
        lookbackDays,
      });
      setResult(res?.data || res);
    } catch {}
    setLoading(false);
  }

  function PortfolioEditor({ rows, setRows, label }) {
    const sum = rows.reduce((s, r) => s + Number(r.weight || 0), 0);
    return (
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white">{label}</h3>
          <span className={`text-xs ${Math.abs(sum - 1) < 0.001 ? 'text-emerald-400' : 'text-red-400'}`}>
            sum = {sum.toFixed(2)}
          </span>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={r.asset} onChange={e => {
                const next = rows.slice(); next[i] = { ...next[i], asset: e.target.value }; setRows(next);
              }} placeholder="BTC" maxLength={10}
                className="w-20 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-xs font-mono focus:border-accent-primary focus:outline-none" />
              <input type="number" step={0.05} min={0} max={1} value={r.weight} onChange={e => {
                const next = rows.slice(); next[i] = { ...next[i], weight: Number(e.target.value) }; setRows(next);
              }}
                className="w-20 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-white text-xs focus:border-accent-primary focus:outline-none" />
              <span className="text-gray-500 text-xs flex-1">{(r.weight * 100).toFixed(0)}%</span>
              <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-gray-600 hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button onClick={() => setRows([...rows, { asset: '', weight: 0 }])}
            className="text-xs text-accent-primary hover:underline flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add asset
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="!p-4 border-blue-500/20 bg-blue-500/5">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-blue-300/90">
            Side-by-side metrics computed from real Binance daily OHLCV. Sharpe assumes risk-free rate = 0 (crypto convention).
            Assets without real price history are excluded and disclosed.
          </div>
        </div>
      </Card>

      {/* Starter presets — one-click loads a worked scenario */}
      <Card className="!p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Start from a preset</span>
          <InfoTip side="right">One-click fills both portfolio editors with a worked scenario. You can edit any row after. Click Compute scenario to see the side-by-side metrics.</InfoTip>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {WHATIF_PRESETS.map(p => (
            <button key={p.id} type="button" onClick={() => applyPreset(p)}
              className="text-left bg-dark-800 hover:bg-dark-700 border border-dark-600 hover:border-accent-primary/40 rounded-lg p-2.5 transition-all">
              <div className="text-xs font-semibold text-white">{p.label}</div>
              <div className="text-[10px] text-gray-500 leading-snug mt-0.5">{p.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PortfolioEditor rows={currentRows} setRows={setCurrentRows} label="Current portfolio" />
        <PortfolioEditor rows={proposedRows} setRows={setProposedRows} label="Proposed portfolio" />
      </div>

      <Card>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            Lookback window
            <InfoTip side="top">30d = noisy, useful for recent regime questions. 90d (default) = the standard window. 180d covers most mini-cycles. 365d covers a full cycle phase. Longer = more stable metrics but may dilute recent regime signal.</InfoTip>
          </label>
          <select value={lookbackDays} onChange={e => setLookbackDays(Number(e.target.value))}
            className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm">
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
          <button onClick={compute} disabled={!sumsValid || loading}
            className="ml-auto px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            Compute scenario
          </button>
        </div>
        {!sumsValid && (
          <p className="text-xs text-red-400 mt-2">Both weight sets must sum to 1.0 (currently {currentSum.toFixed(2)} and {proposedSum.toFixed(2)})</p>
        )}
      </Card>

      {result?.current?.portfolio && result?.proposed?.portfolio && result?.deltas && (
        <Card>
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
            Side-by-side ({result.lookbackDays}d {result.timeframe}, n={result.current.portfolio.sampleDays})
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-gray-500 uppercase tracking-wider border-b border-dark-700/50">
                <th className="text-left py-2 px-3">Metric</th>
                <th className="text-right py-2 px-3">Current</th>
                <th className="text-right py-2 px-3">Proposed</th>
                <th className="text-right py-2 px-3">Δ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700/30 text-xs">
              {[
                { label: 'Annualized return',     cur: result.current.portfolio.annualizedReturn,     prop: result.proposed.portfolio.annualizedReturn,     delta: result.deltas.annReturnDelta,    pct: true,  better: 'high' },
                { label: 'Annualized volatility', cur: result.current.portfolio.annualizedVolatility, prop: result.proposed.portfolio.annualizedVolatility, delta: result.deltas.volatilityDelta,    pct: true,  better: 'low' },
                { label: 'Sharpe ratio',          cur: result.current.portfolio.sharpeRatio,           prop: result.proposed.portfolio.sharpeRatio,          delta: result.deltas.sharpeDelta,        pct: false, better: 'high' },
                { label: 'Max drawdown',          cur: result.current.portfolio.maxDrawdown,           prop: result.proposed.portfolio.maxDrawdown,          delta: result.deltas.maxDrawdownDelta,   pct: true,  better: 'low' },
                { label: 'VaR 95% (daily)',       cur: result.current.portfolio.var95Daily,            prop: result.proposed.portfolio.var95Daily,           delta: result.deltas.var95DailyDelta,    pct: true,  better: 'high' },
              ].map(row => {
                const fmt = (v) => row.pct ? `${(v * 100).toFixed(2)}%` : v.toFixed(3);
                const goodDelta = (row.better === 'high' && row.delta > 0) || (row.better === 'low' && row.delta < 0);
                const dColor = Math.abs(row.delta) < 0.001 ? 'text-gray-400' : (goodDelta ? 'text-emerald-400' : 'text-red-400');
                return (
                  <tr key={row.label} className="hover:bg-dark-700/20">
                    <td className="py-2 px-3 text-white">{row.label}</td>
                    <td className="py-2 px-3 text-right font-mono text-gray-300">{fmt(row.cur)}</td>
                    <td className="py-2 px-3 text-right font-mono text-white">{fmt(row.prop)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${dColor}`}>
                      {row.delta >= 0 ? '+' : ''}{fmt(row.delta)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(result.current.skippedAssets?.length > 0 || result.proposed.skippedAssets?.length > 0) && (
            <div className="mt-3 bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2">
              <p className="text-[11px] text-amber-300">
                Skipped (no real price history): {[...new Set([...(result.current.skippedAssets || []), ...(result.proposed.skippedAssets || [])])].join(', ')}.
                Weights normalized to priced assets only.
              </p>
            </div>
          )}

          <p className="text-[10px] text-gray-600 mt-3">{result.note}</p>
        </Card>
      )}
    </div>
  );
}

// ─── My Lots tab ─────────────────────────────────────────────────────────────

function LotsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try { const res = await getTaxLots(); setData(res?.data || res); } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Spinner className="text-accent-primary" /></div>;
  if (!data) return <Card className="text-center py-12 text-sm text-gray-400">No fill data yet.</Card>;

  const t = data.totals || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <p className="text-xs uppercase text-gray-500 flex items-center gap-1.5">
            Open cost basis
            <InfoTip side="bottom">What you originally paid (in USD) for everything still held. Sum across all open lots. Excludes closed positions.</InfoTip>
          </p>
          <p className="text-lg font-bold text-white">{fmtUsd(t.openCostBasisUsd)}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-gray-500 flex items-center gap-1.5">
            Open market value
            <InfoTip side="bottom">What those same open holdings are worth right now at live prices. The difference vs cost basis is your unrealized P&L.</InfoTip>
          </p>
          <p className="text-lg font-bold text-white">{fmtUsd(t.openMarketValueUsd)}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-gray-500 flex items-center gap-1.5">
            Unrealized P&L
            <InfoTip side="bottom">Paper gain/loss on positions you still hold. Becomes "realized" the moment you sell. Negative number here = harvestable losses available.</InfoTip>
          </p>
          <p className={`text-lg font-bold ${t.unrealizedPnlUsd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtUsd(t.unrealizedPnlUsd)}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-gray-500 flex items-center gap-1.5">
            Realized P&L
            <InfoTip side="bottom">Sum of gains/losses already locked in from closed positions in the current tax year. This is what your CPA actually files.</InfoTip>
          </p>
          <p className={`text-lg font-bold ${t.realizedPnlUsd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtUsd(t.realizedPnlUsd)}</p>
        </Card>
      </div>

      <Card>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Open lots ({data.openLots.length})</h3>
        {data.openLots.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No open lots — all positions are closed.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-gray-500 uppercase border-b border-dark-700/50">
                  <th className="text-left py-2 px-2">Asset</th>
                  <th className="text-right py-2 px-2">Qty</th>
                  <th className="text-right py-2 px-2">Cost basis</th>
                  <th className="text-right py-2 px-2">Now</th>
                  <th className="text-right py-2 px-2">Unrealized</th>
                  <th className="text-left py-2 px-2">Opened</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700/30">
                {data.openLots.map(l => (
                  <tr key={l.lotId} className="hover:bg-dark-700/20">
                    <td className="py-2 px-2 font-mono text-white">{l.base}</td>
                    <td className="py-2 px-2 text-right font-mono text-gray-300">{l.baseRemaining}</td>
                    <td className="py-2 px-2 text-right font-mono text-gray-300">{fmtUsd(l.costBasisUsd)}</td>
                    <td className="py-2 px-2 text-right font-mono text-white">{l.unpriceable ? '—' : fmtUsd(l.currentValueUsd)}</td>
                    <td className={`py-2 px-2 text-right font-mono font-bold ${l.unpriceable ? 'text-gray-500' : (l.unrealizedPnlUsd >= 0 ? 'text-emerald-400' : 'text-red-400')}`}>
                      {l.unpriceable ? '—' : `${l.unrealizedPnlUsd >= 0 ? '+' : ''}${fmtUsd(l.unrealizedPnlUsd)} (${fmtPct(l.unrealizedPnlPct)})`}
                    </td>
                    <td className="py-2 px-2 text-gray-500">{l.openedAt ? new Date(l.openedAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data.unsupportedPairs?.length > 0 && (
        <Card className="!p-4 border-amber-500/20 bg-amber-500/5">
          <p className="text-xs text-amber-300">
            <strong>Skipped fills (non-stable quote):</strong> {data.unsupportedPairs.map(u => `${u.pair} (${u.fillsSkipped})`).join(', ')}.
            Lots from these pairs need historical quote-currency pricing — coming post-launch.
          </p>
        </Card>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function TaxStrategyPage() {
  const [activeTab, setActiveTab] = useState('harvest');

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
          <Receipt className="w-6 h-6" /> Tax & Strategy
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            PRO
          </span>
        </h1>
        <p className="text-gray-400 text-sm mt-1 max-w-3xl leading-relaxed">
          Find harvestable losses across your real fills — keep market exposure with conservative replacement assets. Run portfolio what-if scenarios against real market OHLCV before clicking execute.
          <strong className="text-emerald-400"> Estimates only — not tax advice.</strong>
        </p>
      </div>

      <div className="flex gap-1 bg-dark-800 p-1 rounded-xl border border-dark-700/50 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-1 justify-center ${
              activeTab === id ? 'bg-accent-primary text-dark-900' : 'text-gray-400 hover:text-white hover:bg-dark-700/60'
            }`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'harvest' && <HarvestTab />}
      {activeTab === 'whatif'  && <WhatIfTab />}
      {activeTab === 'lots'    && <LotsTab />}
    </div>
  );
}
